/**
 * Custom hook for playing notification sounds and browser notifications
 * Uses Web Audio API for reliable cross-browser sound playback
 * Uses Notifications API for desktop alerts when tab is in background
 */
import { useCallback, useRef, useEffect, useState } from 'react';

// Sound types with their frequencies and patterns
const SOUND_CONFIGS = {
  // Short confirmation beep for toggles/actions
  toggle: {
    frequency: 880,
    duration: 0.1,
    type: 'sine',
    volume: 0.3
  },
  // Success notification (two ascending tones)
  success: {
    frequencies: [523, 659],
    duration: 0.15,
    type: 'sine',
    volume: 0.3
  },
  // Error notification (descending tone)
  error: {
    frequencies: [440, 330],
    duration: 0.15,
    type: 'sine',
    volume: 0.3
  },
  // Incoming call ringtone (repeating pattern)
  ringtone: {
    frequencies: [784, 659, 784, 659],
    duration: 0.3,
    gap: 0.1,
    type: 'sine',
    volume: 0.5,
    repeat: true
  },
  // Call connected notification
  callConnected: {
    frequencies: [523, 659, 784],
    duration: 0.1,
    type: 'sine',
    volume: 0.4
  },
  // Call ended notification
  callEnded: {
    frequencies: [784, 523],
    duration: 0.2,
    type: 'sine',
    volume: 0.3
  }
};

export const useNotificationSound = () => {
  const audioContextRef = useRef(null);
  const ringtoneIntervalRef = useRef(null);
  const isPlayingRingtoneRef = useRef(false);
  const activeNotificationRef = useRef(null);
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  // Check if browser supports notifications
  const supportsNotifications = typeof Notification !== 'undefined';

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!supportsNotifications) {
      console.log('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      setNotificationPermission('granted');
      return true;
    }

    if (Notification.permission !== 'denied') {
      try {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        return permission === 'granted';
      } catch (error) {
        console.error('Error requesting notification permission:', error);
        return false;
      }
    }

    return false;
  }, [supportsNotifications]);

  // Check if tab is visible
  const isTabVisible = useCallback(() => {
    return document.visibilityState === 'visible';
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title, options = {}) => {
    if (!supportsNotifications || Notification.permission !== 'granted') {
      return null;
    }

    // Close any existing notification
    if (activeNotificationRef.current) {
      activeNotificationRef.current.close();
    }

    try {
      const notification = new Notification(title, {
        icon: '/logo192.png', // Default app icon
        badge: '/logo192.png',
        vibrate: [200, 100, 200], // Vibration pattern for mobile
        requireInteraction: true, // Keep notification visible until user interacts
        tag: 'aiconneqtors-call', // Replace previous notifications with same tag
        ...options
      });

      // Focus window when notification is clicked
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };

      notification.onclose = () => {
        if (activeNotificationRef.current === notification) {
          activeNotificationRef.current = null;
        }
        if (options.onClose) {
          options.onClose();
        }
      };

      activeNotificationRef.current = notification;
      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }, [supportsNotifications]);

  // Close active notification
  const closeNotification = useCallback(() => {
    if (activeNotificationRef.current) {
      activeNotificationRef.current.close();
      activeNotificationRef.current = null;
    }
  }, []);

  // Show incoming call notification (with persistent desktop alert)
  const showIncomingCallNotification = useCallback((callerNumber, callerName, options = {}) => {
    // Only show desktop notification if tab is not visible
    if (!isTabVisible()) {
      return showBrowserNotification('📞 Incoming Call', {
        body: `${callerName || 'Unknown Caller'}\n${callerNumber || 'Unknown Number'}`,
        tag: 'aiconneqtors-incoming-call',
        requireInteraction: true,
        silent: false, // Allow default notification sound
        ...options
      });
    }
    return null;
  }, [isTabVisible, showBrowserNotification]);

  // Initialize audio context lazily (required for browser autoplay policies)
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (happens after user interaction requirement)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Play a single tone
  const playTone = useCallback((frequency, duration, type = 'sine', volume = 0.3) => {
    try {
      const audioContext = getAudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = type;

      // Envelope for smooth sound
      const now = audioContext.currentTime;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
      gainNode.gain.linearRampToValueAtTime(volume, now + duration - 0.01);
      gainNode.gain.linearRampToValueAtTime(0, now + duration);

      oscillator.start(now);
      oscillator.stop(now + duration);
    } catch (error) {
      console.log('Audio playback error:', error);
    }
  }, [getAudioContext]);

  // Play a sequence of tones
  const playSequence = useCallback((frequencies, duration, type, volume, gap = 0) => {
    frequencies.forEach((freq, index) => {
      setTimeout(() => {
        playTone(freq, duration, type, volume);
      }, index * (duration * 1000 + gap * 1000));
    });
  }, [playTone]);

  // Main play function
  const playSound = useCallback((soundType) => {
    const config = SOUND_CONFIGS[soundType];
    if (!config) {
      console.warn(`Unknown sound type: ${soundType}`);
      return;
    }

    if (config.frequencies) {
      playSequence(config.frequencies, config.duration, config.type, config.volume, config.gap || 0);
    } else {
      playTone(config.frequency, config.duration, config.type, config.volume);
    }
  }, [playTone, playSequence]);

  // Start ringtone (loops until stopped)
  const startRingtone = useCallback((callerInfo = {}) => {
    if (isPlayingRingtoneRef.current) return;
    
    isPlayingRingtoneRef.current = true;
    const config = SOUND_CONFIGS.ringtone;
    
    // Show browser notification for incoming call
    showIncomingCallNotification(callerInfo.number, callerInfo.name);
    
    const playRingtonePattern = () => {
      if (!isPlayingRingtoneRef.current) return;
      
      config.frequencies.forEach((freq, index) => {
        setTimeout(() => {
          if (isPlayingRingtoneRef.current) {
            playTone(freq, config.duration, config.type, config.volume);
          }
        }, index * (config.duration * 1000 + config.gap * 1000));
      });
    };

    // Play immediately
    playRingtonePattern();
    
    // Then repeat every 2 seconds
    ringtoneIntervalRef.current = setInterval(playRingtonePattern, 2000);
  }, [playTone, showIncomingCallNotification]);

  // Stop ringtone
  const stopRingtone = useCallback(() => {
    isPlayingRingtoneRef.current = false;
    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
    // Close any incoming call notification
    closeNotification();
  }, [closeNotification]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRingtone();
      closeNotification();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopRingtone, closeNotification]);

  // Auto-request permission on mount (if not already granted/denied)
  useEffect(() => {
    if (supportsNotifications && Notification.permission === 'default') {
      // Don't auto-request - wait for user interaction
      // This is just to set the initial state
      setNotificationPermission(Notification.permission);
    }
  }, [supportsNotifications]);

  return {
    playSound,
    startRingtone,
    stopRingtone,
    // Browser notification functions
    showBrowserNotification,
    showIncomingCallNotification,
    closeNotification,
    requestNotificationPermission,
    notificationPermission,
    supportsNotifications
  };
};

export default useNotificationSound;
